/**
 * Automatic Discounts Module (Local Calculation) - AE Adapted
 *
 * Calculates automatic discount prices from visible enriched discount flags
 * in the DOM. No Storefront API shadow carts, no cache, no throttle.
 *
 * Flow:
 * 1. Read visible discount flags with data-discount-type/value attributes
 * 2. Calculate candidate final prices locally from current product price
 * 3. Choose the best valid final price (lowest)
 * 4. Update DOM placeholders for price and badge
 *
 * AE Adaptations:
 * - Card selector: ae-product-card
 * - Badge selector: ae-card__badge
 * - PLP price container: ae-card__price
 * - PDP price container: product-price [ref="priceContainer"]
 */

(function () {
  // ─── Constants ───────────────────────────────────────────────

  var MUTATION_DEBOUNCE = 300; // ms debounce for MutationObserver
  var FORMAT_MONEY_CACHE_MAX = 500; // max entries in formatMoney cache

  // ─── Module State ───────────────────────────────────────────

  var formatMoneyCache = {}; // persistent cache for formatMoney

  // ─── Helpers ─────────────────────────────────────────────────

  /**
   * Formats a price using the store's money_format.
   * Supports multiple placeholder formats with module-level cache.
   */
  function formatMoney(amountInUnits, moneyFormat) {
    var cacheKey = amountInUnits + "|" + moneyFormat;
    if (formatMoneyCache[cacheKey]) {
      return formatMoneyCache[cacheKey];
    }

    var amount = amountInUnits;
    var formatted = amount.toFixed(2);
    var parts = formatted.split(".");
    var dollars = parts[0];
    var cents = parts[1];

    // EU format: dot thousands, comma decimal
    var withComma = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ".") + "," + cents;

    // US format: comma thousands, dot decimal
    var withDot = dollars.replace(/\B(?=(\d{3})+(?!\d))/g, ",") + "." + cents;

    // No decimals
    var rounded = Math.round(amount).toString();
    var roundedWithComma = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
    var roundedWithDot = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    var roundedWithApostrophe = rounded.replace(/\B(?=(\d{3})+(?!\d))/g, "'");

    // Swiss format: apostrophe thousands, dot decimal
    var withApostrophe =
      dollars.replace(/\B(?=(\d{3})+(?!\d))/g, "'") + "." + cents;

    var placeholderMap = {
      amount: withDot,
      amount_with_comma_separator: withComma,
      amount_with_apostrophe_separator: withApostrophe,
      amount_no_decimals: roundedWithDot,
      amount_no_decimals_with_comma_separator: roundedWithComma,
      amount_no_decimals_with_apostrophe_separator: roundedWithApostrophe,
    };

    var result = moneyFormat.replace(
      /\{\{\s*(\w+)\s*\}\}/g,
      function (match, key) {
        var value = placeholderMap[key];
        return typeof value === "string" ? value : match;
      }
    );

    // Defensive cache reset if it grows too large
    if (Object.keys(formatMoneyCache).length >= FORMAT_MONEY_CACHE_MAX) {
      formatMoneyCache = {};
    }
    formatMoneyCache[cacheKey] = result;
    return result;
  }

  // ─── Discount Calculation (Local) ────────────────────────────

  /**
   * Finds visible discount flags with calculation metadata.
   * @param {Element} root - Container to search within
   * @returns {Array<Element>} - Visible flags with data-discount-type/value
   */
  function findVisibleDiscountFlags(root) {
    if (!root) return [];

    var flags = root.querySelectorAll("[data-discount-type][data-discount-value]");
    var visible = [];

    for (var i = 0; i < flags.length; i++) {
      var flag = flags[i];
      // Only include flags that are NOT hidden
      if (!flag.hasAttribute("hidden")) {
        visible.push(flag);
      }
    }

    return visible;
  }

  /**
   * Finds PDP containers that can own visible discount flags for a product-price block.
   * Keeps lookup scoped to the current product context instead of using global galleries.
   * @param {Element} productPrice - PDP product-price element
   * @returns {Array<Element>} - Roots to search for visible discount flags
   */
  function getPDPDiscountFlagRoots(productPrice) {
    var roots = [];

    function addRoot(root) {
      if (root && roots.indexOf(root) === -1) {
        roots.push(root);
      }
    }

    addRoot(productPrice);

    if (!productPrice || !productPrice.closest) return roots;

    var scopeSelectors = [
      "quick-add-modal, dialog",
      ".shopify-section, section",
      "main"
    ];

    for (var scopeIndex = 0; scopeIndex < scopeSelectors.length; scopeIndex++) {
      var scope = productPrice.closest(scopeSelectors[scopeIndex]);
      if (!scope) continue;

      var galleryContainers = scope.querySelectorAll(".gallery-main-container");
      for (
        var galleryIndex = 0;
        galleryIndex < galleryContainers.length;
        galleryIndex++
      ) {
        addRoot(galleryContainers[galleryIndex]);
      }
    }

    return roots;
  }

  /**
   * Parses a discount candidate from a flag element.
   * @param {Element} flag - Flag with data-discount-type/value
   * @param {number} originalCents - Original price in cents
   * @returns {Object|null} - Candidate with finalPriceUnits, type, value; null if invalid
   */
  function parseDiscountCandidate(flag, originalCents) {
    if (!flag || originalCents <= 0) return null;

    var type = flag.getAttribute("data-discount-type");
    var valueStr = flag.getAttribute("data-discount-value");

    if (!type || !valueStr) return null;

    var value = parseFloat(valueStr);
    if (isNaN(value) || value <= 0) return null;

    // Convert original price to money units for calculation
    var originalUnits = originalCents / 100;
    var finalPriceUnits;

    if (type === "percentage") {
      // percentage: finalPrice = originalPrice * (1 - discount_value / 100)
      finalPriceUnits = originalUnits * (1 - value / 100);
    } else if (type === "fixed_amount") {
      // fixed_amount: discount_value is in cents, subtract from money units
      finalPriceUnits = originalUnits - (value / 100);
    } else {
      // Unknown discount type
      return null;
    }

    // Validate: must be positive and lower than original
    if (finalPriceUnits <= 0 || finalPriceUnits >= originalUnits) {
      return null;
    }

    return {
      type: type,
      value: value,
      finalPriceUnits: finalPriceUnits,
      flag: flag
    };
  }

  /**
   * Chooses the best valid final price from candidates and manual compare-at.
   * @param {Array<Object>} candidates - Parsed discount candidates
   * @param {number} originalCents - Original price in cents
   * @param {number} compareAtCents - Manual compare-at price in cents
   * @returns {Object|null} - Best result with finalPriceUnits and source; null if no valid discount
   */
  function chooseBestFinalPrice(candidates, originalCents, compareAtCents) {
    if (!candidates || candidates.length === 0) return null;

    var originalUnits = originalCents / 100;
    var bestCandidate = null;
    var bestPriceUnits = null;

    // Find lowest valid automatic candidate
    for (var i = 0; i < candidates.length; i++) {
      var candidate = candidates[i];
      if (candidate.finalPriceUnits < originalUnits) {
        if (bestPriceUnits === null || candidate.finalPriceUnits < bestPriceUnits) {
          bestPriceUnits = candidate.finalPriceUnits;
          bestCandidate = candidate;
        }
      }
    }

    // Check manual compare-at pricing competition
    var hasManualDiscount = compareAtCents > 0 && compareAtCents > originalCents;
    var manualPriceUnits = hasManualDiscount ? originalUnits : null;

    // Choose winner
    if (bestCandidate && bestPriceUnits !== null) {
      // Automatic discount candidate exists
      if (hasManualDiscount) {
        // Compare: lowest wins
        if (bestPriceUnits < manualPriceUnits) {
          return {
            finalPriceUnits: bestPriceUnits,
            source: "automatic",
            candidate: bestCandidate
          };
        } else {
          // Manual is better or equal - no automatic display
          return {
            finalPriceUnits: manualPriceUnits,
            source: "manual"
          };
        }
      } else {
        // No manual discount, automatic wins
        return {
          finalPriceUnits: bestPriceUnits,
          source: "automatic",
          candidate: bestCandidate
        };
      }
    } else if (hasManualDiscount) {
      // Only manual discount
      return {
        finalPriceUnits: manualPriceUnits,
        source: "manual"
      };
    }

    // No valid discount
    return null;
  }

  // ─── DOM Helpers ─────────────────────────────────────────────

  /**
   * Resets price container classes from previous runs.
   */
  function resetPriceContainerClasses(priceContainer) {
    if (!priceContainer) return;

    var contaminatedCompareEls = priceContainer.querySelectorAll(
      ".price--compare-hidden"
    );
    for (var ce = 0; ce < contaminatedCompareEls.length; ce++) {
      contaminatedCompareEls[ce].classList.remove("price--compare-hidden");
    }

    var contaminatedAutoEls = priceContainer.querySelectorAll(
      ".price--compare-auto"
    );
    for (var ae = 0; ae < contaminatedAutoEls.length; ae++) {
      contaminatedAutoEls[ae].classList.remove("price--compare-auto");
    }

    // Remove dynamically-added ae-card__price-compare from current price elements
    var contaminatedCardCompareEls = priceContainer.querySelectorAll(
      ".ae-card__price-current.ae-card__price-compare"
    );
    for (var cc = 0; cc < contaminatedCardCompareEls.length; cc++) {
      contaminatedCardCompareEls[cc].classList.remove("ae-card__price-compare");
    }
  }

  /**
   * Hides a badge element if it exists.
   */
  function hideBadge(badgeEl) {
    if (badgeEl) badgeEl.setAttribute("hidden", "");
  }

  /**
   * Gets the money format from theme config.
   * Uses non-API __moneyFormat global exposed in layout/theme.liquid.
   */
  function getMoneyFormat() {
    // Primary: non-API money format global
    if (window.__moneyFormat) {
      return window.__moneyFormat;
    }
    // Default fallback
    return "{{amount}}";
  }

  /**
   * Shows discounted price and strikes original.
   */
  function showDiscountedPrice(
    priceContainer,
    finalPriceUnits,
    moneyFormat,
    currentPriceEl
  ) {
    var autoDiscountPriceEl = priceContainer.querySelector(
      "[data-auto-discount-price]"
    );
    if (!currentPriceEl) {
      currentPriceEl = priceContainer.querySelector(
        ".ae-card__price-current, .custom-product-price__current"
      );
    }

    if (!currentPriceEl || !autoDiscountPriceEl) return;

    // Strike original price
    if (currentPriceEl.classList.contains("custom-product-price__current")) {
      currentPriceEl.classList.add("price--compare-auto");
    } else {
      currentPriceEl.classList.add("ae-card__price-compare");
    }

    // Show automatic price
    autoDiscountPriceEl.textContent = formatMoney(finalPriceUnits, moneyFormat);
    autoDiscountPriceEl.removeAttribute("hidden");
  }

  /**
   * Synchronizes manual sale badge based on compare_at_price.
   * Shows the final price (with discount) instead of "% Off".
   */
  function syncManualSaleBadge(card, moneyFormat) {
    if (!card) return;

    var staticBadge = card.querySelector(
      ".ae-card__badge[data-discount-badge]"
    );

    if (!staticBadge) return;

    var compareAtPriceValue = parseInt(
      card.getAttribute("data-compare-at-price") || "0",
      10
    );
    var productPriceValue = parseInt(
      card.getAttribute("data-product-price") || "0",
      10
    );

    if (compareAtPriceValue > productPriceValue) {
      // Show final price in badge instead of percent
      staticBadge.textContent = formatMoney(productPriceValue / 100, moneyFormat);
      staticBadge.removeAttribute("hidden");
      return;
    }

    staticBadge.setAttribute("hidden", "");
  }

  /**
   * Resets automatic discount visual state for a card.
   */
  function resetCardDiscountState(card, moneyFormat) {
    if (!card) return;

    var priceContainer = card.querySelector(".ae-card__price");

    if (priceContainer) {
      resetPriceContainerClasses(priceContainer);

      var autoDiscountPriceEl = priceContainer.querySelector(
        "[data-auto-discount-price]"
      );
      if (autoDiscountPriceEl) {
        autoDiscountPriceEl.textContent = "";
        autoDiscountPriceEl.setAttribute("hidden", "");
      }
    }

    hideBadge(card.querySelector(".ae-card__badge[data-auto-discount-badge]"));

    // Discount flags are already visible from Liquid - no need to hide them

    syncManualSaleBadge(card, moneyFormat);
  }

  // ─── Apply to PLP Card ───────────────────────────────────────

  /**
   * Applies local discount calculation to a PLP card.
   * Reads visible flags, calculates best price, updates DOM.
   * @param {Element} card - ae-product-card element
   */
  function applyLocalDiscountToCard(card) {
    if (!card) return;

    var moneyFormat = getMoneyFormat();

    // Get price data from card attributes (cents)
    var originalCents = parseInt(
      card.getAttribute("data-product-price") || "0",
      10
    );
    var compareAtCents = parseInt(
      card.getAttribute("data-compare-at-price") || "0",
      10
    );

    // Reset state BEFORE validating (prevents stale UI from previous run)
    resetCardDiscountState(card, moneyFormat);

    if (originalCents <= 0) return;

    // Find visible discount flags in card
    var flags = findVisibleDiscountFlags(card);

    // Parse candidates
    var candidates = [];
    for (var i = 0; i < flags.length; i++) {
      var candidate = parseDiscountCandidate(flags[i], originalCents);
      if (candidate) {
        candidates.push(candidate);
      }
    }

    // Choose best price
    var best = chooseBestFinalPrice(candidates, originalCents, compareAtCents);

    if (!best) {
      // No valid discount - done
      return;
    }

    var priceContainer = card.querySelector(".ae-card__price");
    if (!priceContainer) return;

    if (best.source === "automatic") {
      // Automatic discount wins

      // Hide static manual sale badge
      var staticBadge = card.querySelector(".ae-card__badge[data-discount-badge]");
      hideBadge(staticBadge);

      // When automatic pricing wins, the product's current price becomes the
      // struck reference price. If a Shopify compare-at price is present, hide
      // that older reference to avoid showing three competing prices.
      var manualCompareEl = priceContainer.querySelector(
        ".ae-card__price-compare:not([data-auto-discount-price])"
      );
      if (manualCompareEl) {
        manualCompareEl.classList.add("price--compare-hidden");
      }

      // Show automatic price and strike the current/base price.
      showDiscountedPrice(priceContainer, best.finalPriceUnits, moneyFormat);

      // Auto-discount badge stays hidden (matching flag is visible from Liquid)
      hideBadge(card.querySelector(".ae-card__badge[data-auto-discount-badge]"));

      // Emit custom-price sync event
      document.dispatchEvent(
        new CustomEvent("custom-price:sync", {
          detail: {
            card: card,
            effectivePrice: Math.round(best.finalPriceUnits * 100)
          }
        })
      );
    } else if (best.source === "manual") {
      // Manual discount wins - syncManualSaleBadge already handled it
      syncManualSaleBadge(card, moneyFormat);
    }
  }

  // ─── Apply to PDP ─────────────────────────────────────────────

  /**
   * Applies local discount calculation to PDP price block.
   * @param {Element} productPrice - product-price element
   */
  function applyLocalDiscountToPDP(productPrice) {
    if (!productPrice) return;

    var moneyFormat = getMoneyFormat();

    // Get price data from product-price attributes (cents)
    var originalCents = parseInt(
      productPrice.getAttribute("data-product-price") || "0",
      10
    );
    var compareAtCents = parseInt(
      productPrice.getAttribute("data-compare-at-price") || "0",
      10
    );

    // Reset PDP discount state BEFORE validating (prevents stale UI from previous run)
    var priceContainer = productPrice.querySelector('[ref="priceContainer"]');
    if (priceContainer) {
      resetPriceContainerClasses(priceContainer);

      var autoDiscountPriceEl = priceContainer.querySelector(
        "[data-auto-discount-price]"
      );
      if (autoDiscountPriceEl) {
        autoDiscountPriceEl.textContent = "";
        autoDiscountPriceEl.setAttribute("hidden", "");
      }
    }

    // Reset badges
    hideBadge(priceContainer
      ? priceContainer.querySelector("[data-auto-discount-badge-pdp]")
      : null);

    var manualDiscountBadge = priceContainer
      ? priceContainer.querySelector(".custom-product-price__badge--off")
      : null;
    var manualSaleBadge = priceContainer
      ? priceContainer.querySelector(".custom-product-price__badge--sale")
      : null;

    if (manualDiscountBadge) manualDiscountBadge.removeAttribute("hidden");
    if (manualSaleBadge) manualSaleBadge.removeAttribute("hidden");

    if (originalCents <= 0) return;

    // Find visible discount flags in the current PDP context (gallery + price area)
    var flagRoots = getPDPDiscountFlagRoots(productPrice);
    var allFlags = [];
    for (var rootIndex = 0; rootIndex < flagRoots.length; rootIndex++) {
      var rootFlags = findVisibleDiscountFlags(flagRoots[rootIndex]);
      for (var flagIndex = 0; flagIndex < rootFlags.length; flagIndex++) {
        if (allFlags.indexOf(rootFlags[flagIndex]) === -1) {
          allFlags.push(rootFlags[flagIndex]);
        }
      }
    }

    // Parse candidates
    var candidates = [];
    for (var i = 0; i < allFlags.length; i++) {
      var candidate = parseDiscountCandidate(allFlags[i], originalCents);
      if (candidate) {
        candidates.push(candidate);
      }
    }

    // Choose best price
    var best = chooseBestFinalPrice(candidates, originalCents, compareAtCents);

    if (!best) {
      // No valid discount
      return;
    }

    if (best.source === "automatic") {
      // Automatic discount wins

      // Hide manual badges
      if (manualDiscountBadge) hideBadge(manualDiscountBadge);
      if (manualSaleBadge) hideBadge(manualSaleBadge);

      // Hide manual compare price
      var manualCompareEl = priceContainer
        ? priceContainer.querySelector(".custom-product-price__compare")
        : null;
      if (manualCompareEl) {
        manualCompareEl.classList.add("price--compare-hidden");
      }

      // Show automatic price
      if (priceContainer) {
        showDiscountedPrice(priceContainer, best.finalPriceUnits, moneyFormat);
      }

      // Emit custom-price sync for PDP
      var customPriceEl = productPrice.querySelector(".custom-price");
      if (customPriceEl) {
        var configStr = customPriceEl.getAttribute("data-custom-price-config");
        if (configStr) {
          try {
            var customConfig = JSON.parse(configStr);
            if (
              customConfig.accumulates === true &&
              typeof best.finalPriceUnits === "number"
            ) {
              document.dispatchEvent(
                new CustomEvent("custom-price:sync-pdp", {
                  detail: {
                    host: productPrice,
                    effectivePrice: Math.round(best.finalPriceUnits * 100)
                  }
                })
              );
            }
          } catch (e) {
            console.warn("[AutoDiscounts] Invalid data-custom-price-config JSON:", e);
          }
        }
      }
    } else if (best.source === "manual") {
      // Manual discount wins - badges already visible
    }
  }

  // ─── Initialization PLP ──────────────────────────────────────

  /**
   * Processes all initial PLP cards on page load.
   */
  function processInitialCards() {
    var cards = document.querySelectorAll("ae-product-card[data-variant-id]");
    for (var i = 0; i < cards.length; i++) {
      applyLocalDiscountToCard(cards[i]);
    }
  }

  // ─── MutationObserver (Dynamic Cards) ────────────────────────

  var debounceTimer = null;
  var pendingMutationCards = [];

  /**
   * Gets the product container for MutationObserver scope.
   */
  function getProductContainer() {
    var containers = [
      document.querySelector(".product-grid"),
      document.querySelector("[product-grid]"),
      document.querySelector("#MainContent"),
      document.querySelector("main")
    ];

    for (var i = 0; i < containers.length; i++) {
      if (containers[i]) return containers[i];
    }

    return document.body;
  }

  /**
   * Handles newly inserted cards after debounce.
   */
  function flushPendingCards() {
    var cards = pendingMutationCards.slice();
    pendingMutationCards = [];

    // Dedupe by element reference using indexOf (safe for DOM elements)
    var uniqueCards = [];
    for (var i = 0; i < cards.length; i++) {
      if (uniqueCards.indexOf(cards[i]) === -1) {
        uniqueCards.push(cards[i]);
      }
    }

    for (var j = 0; j < uniqueCards.length; j++) {
      applyLocalDiscountToCard(uniqueCards[j]);
    }
  }

  /**
   * Observes dynamically inserted product cards (infinite scroll, filters).
   */
  function observeNewCards() {
    var observer = new MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var addedNodes = mutations[i].addedNodes;
        for (var j = 0; j < addedNodes.length; j++) {
          var node = addedNodes[j];
          if (node.nodeType !== 1) continue;

          // Node is an ae-product-card
          if (
            node.matches &&
            node.matches("ae-product-card[data-variant-id]")
          ) {
            pendingMutationCards.push(node);
          }

          // Node contains ae-product-cards
          if (node.querySelector) {
            var innerCards = node.querySelectorAll(
              "ae-product-card[data-variant-id]"
            );
            for (var k = 0; k < innerCards.length; k++) {
              pendingMutationCards.push(innerCards[k]);
            }
          }
        }
      }

      if (pendingMutationCards.length === 0) return;

      clearTimeout(debounceTimer);
      debounceTimer = setTimeout(flushPendingCards, MUTATION_DEBOUNCE);
    });

    var container = getProductContainer();
    observer.observe(container, { childList: true, subtree: true });
  }

  // ─── Variant Update Handler (PDP) ────────────────────────────

  /**
   * Updates variant attributes on card when selection changes.
   */
  function syncCardVariantAttributes(card, resource) {
    if (!card || !resource) return;

    if (resource.id != null) {
      card.setAttribute("data-variant-id", String(resource.id));
    }

    if (resource.price != null) {
      card.setAttribute("data-product-price", String(resource.price));
    }

    card.setAttribute(
      "data-compare-at-price",
      String(resource.compare_at_price || 0)
    );
  }

  /**
   * Handles variant:update events from theme (PLP cards and PDP).
   */
  function handleThemeVariantUpdate(event) {
    var detail = event.detail;
    var target = event.target;

    // PLP card variant change
    var card =
      target && target.closest ? target.closest("ae-product-card") : null;

    if (card && detail && detail.resource) {
      syncCardVariantAttributes(card, detail.resource);

      requestAnimationFrame(function () {
        applyLocalDiscountToCard(card);
      });
      return;
    }

    // PDP variant change - variant-picker dispatches the event, so we need to find product-price in DOM
    // NOT via event.target.closest("product-price") because variant-picker is not inside product-price
    if (!detail || !detail.resource) return;

    // Find PDP product-price element using productId from event detail.data
    // VariantUpdateEvent structure: detail.data.productId (not detail.productId)
    var productId = (detail.data && detail.data.productId) || null;
    var productPrice = null;

    if (target && target.closest) {
      var scope = target.closest(
        "section, product-info, product-form-component, quick-add-modal, dialog, .shopify-section, main"
      );
      if (scope) {
        if (productId) {
          productPrice = scope.querySelector(
            "product-price[data-product-id=\"" + productId + "\"]"
          );
        }

        if (!productPrice) {
          var scopedProductPrices = scope.querySelectorAll(
            "product-price[data-variant-id]"
          );
          if (scopedProductPrices.length === 1) {
            productPrice = scopedProductPrices[0];
          }
        }
      }
    }

    if (productId) {
      var matchingProductPrices = document.querySelectorAll(
        "product-price[data-product-id=\"" + productId + "\"]"
      );
      if (!productPrice && matchingProductPrices.length === 1) {
        productPrice = matchingProductPrices[0];
      }
    }

    // Fallback: single product-price context (safe when only one exists)
    // Do NOT use if multiple product-price elements could be present
    if (!productPrice) {
      var allProductPrices = document.querySelectorAll("product-price[data-variant-id]");
      // Safe fallback only when single instance exists
      if (allProductPrices.length === 1) {
        productPrice = allProductPrices[0];
      }
    }

    if (!productPrice) return;

    // Update product-price attributes
    if (detail.resource.id != null) {
      productPrice.setAttribute("data-variant-id", String(detail.resource.id));
    }
    if (detail.resource.price != null) {
      productPrice.setAttribute(
        "data-product-price",
        String(detail.resource.price)
      );
    }
    productPrice.setAttribute(
      "data-compare-at-price",
      String(detail.resource.compare_at_price || 0)
    );

    // Update gallery variant ID if present
    var galleryContainer = document.querySelector(
      ".gallery-main-container[data-variant-id]"
    );
    if (galleryContainer && detail.resource.id != null) {
      galleryContainer.setAttribute(
        "data-variant-id",
        String(detail.resource.id)
      );
    }

    // Recalculate PDP discount
    requestAnimationFrame(function () {
      applyLocalDiscountToPDP(productPrice);
    });
  }

  // ─── Initialization PDP ──────────────────────────────────────

  /**
   * Processes PDP on initial load.
   */
  function processPDP() {
    var productPrice = document.querySelector("product-price[data-variant-id]");
    if (!productPrice) return;

    applyLocalDiscountToPDP(productPrice);
  }

  // ─── Arranque ────────────────────────────────────────────────

  /**
   * Main initialization.
   */
  function init() {
    processInitialCards();
    processPDP();
    observeNewCards();
    document.addEventListener("variant:update", handleThemeVariantUpdate, true);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
